import { useState } from 'react';

import { RiGlobalLine } from '@remixicon/react';
import classNames from 'classnames';

import { Image } from '@spotify-internal/encore-web/next';
import {
  type ToolGroupEntry,
  type ToolIconRenderOptions,
  ToolLabel,
  type ToolRenderOptions,
  ToolResultMessage,
  createToolExtension,
} from '@spotify/plugin-confidence-chat-react';

import { confidenceChatPlugin } from '../plugin';
import { formatLineCount } from './formatLineCount';
import {
  formatToolResultContent,
  getInputString,
  getToolInput,
  parseToolResultObject,
  truncateToolSubject,
} from './parseToolResult';

import styles from './WebResearchToolExtensions.module.css';

type WebResearchTool = 'web_search' | 'web_fetch';

const webResearchTools = {
  web_search: {
    inputKey: 'query',
    streamingLabel: 'Searching',
    runningLabel: 'Searching for',
    completedLabel: 'Searched for',
  },
  web_fetch: {
    inputKey: 'url',
    streamingLabel: 'Fetching',
    runningLabel: 'Fetching',
    completedLabel: 'Fetched',
  },
} satisfies Record<
  WebResearchTool,
  {
    inputKey: string;
    streamingLabel: string;
    runningLabel: string;
    completedLabel: string;
  }
>;

const getFetchUrl = (url: string): URL | undefined => {
  try {
    const parsed = new URL(url);
    return ['http:', 'https:'].includes(parsed.protocol) ? parsed : undefined;
  } catch {
    return undefined;
  }
};

const WebFetchPlaceholderIcon = ({ className }: { className?: string }) => (
  <RiGlobalLine
    className={className}
    aria-hidden
    data-testid="web-fetch-placeholder"
  />
);

const WebFetchIcon = ({
  src,
  size,
  className,
}: Pick<ToolIconRenderOptions, 'size' | 'className'> & { src: string }) => {
  const [status, setStatus] = useState<'loading' | 'loaded' | 'failed'>(
    'loading',
  );
  if (status === 'failed')
    return (
      <RiGlobalLine
        size={size}
        className={className}
        aria-hidden
        data-testid="web-fetch-fallback"
      />
    );

  return (
    <Image
      src={src}
      alt=""
      aria-hidden
      width={`${size}px`}
      height={`${size}px`}
      className={classNames(className, styles.favicon)}
      borderRadius="round"
      placeholderIcon={
        status === 'loaded' ? undefined : WebFetchPlaceholderIcon
      }
      referrerPolicy="no-referrer"
      decoding="async"
      imageRef={image => {
        if (image?.complete && image.naturalWidth > 0) setStatus('loaded');
      }}
      onLoad={() => setStatus('loaded')}
      onError={() => setStatus('failed')}
    />
  );
};

const renderWebFetchIcon = (
  entry: ToolGroupEntry,
  options: ToolIconRenderOptions,
) => {
  if (options.isStreaming) return null;
  const url = getInputString(getToolInput(entry.input), 'url');
  const hostname = url ? getFetchUrl(url)?.hostname : undefined;
  if (!hostname) return null;
  const src = `https://icons.duckduckgo.com/ip3/${encodeURIComponent(hostname)}.ico`;
  return <WebFetchIcon key={src} src={src} {...options} />;
};

const WebResearchToolLabel = ({
  tool,
  entry,
  options,
}: {
  tool: WebResearchTool;
  entry: ToolGroupEntry;
  options: ToolRenderOptions;
}) => {
  const config = webResearchTools[tool];
  const subject = getInputString(getToolInput(entry.input), config.inputKey);
  let action = config.runningLabel;
  if (options.isStreaming) action = config.streamingLabel;
  else if (entry.result?.success) action = config.completedLabel;

  const resultData = entry.result?.success
    ? parseToolResultObject(entry.result.content)
    : undefined;
  const detailsContent =
    tool === 'web_fetch' && typeof resultData?.content === 'string'
      ? resultData.content
      : formatToolResultContent(entry.result?.content ?? '');
  let summary: string | undefined;
  if (entry.result) {
    if (!entry.result.success) summary = 'Failed';
    else {
      if (tool === 'web_search' && Array.isArray(resultData?.results)) {
        const count = resultData.results.length;
        summary = `${count} ${count === 1 ? 'result' : 'results'}`;
      } else if (tool === 'web_fetch') {
        summary = formatLineCount(detailsContent);
      }
      if (resultData?.truncated === true) {
        summary = summary ? `${summary} (truncated)` : 'Truncated';
      }
    }
  }

  return (
    <ToolLabel.Root>
      <ToolLabel.Primary>
        {action}
        {!options.isStreaming && subject && (
          <>
            {' '}
            <span title={subject}>
              {truncateToolSubject(
                tool === 'web_fetch'
                  ? (getFetchUrl(subject)?.hostname ?? subject)
                  : subject,
              )}
            </span>
          </>
        )}
      </ToolLabel.Primary>
      {summary && <ToolLabel.Secondary>{summary}</ToolLabel.Secondary>}
      <ToolLabel.Timer startedAt={entry.result ? undefined : entry.startedAt} />
      {entry.result && (
        <ToolLabel.Details>
          <ToolResultMessage
            content={detailsContent}
            success={entry.result.success}
          />
        </ToolLabel.Details>
      )}
    </ToolLabel.Root>
  );
};

const createWebResearchExtension = (tool: WebResearchTool) =>
  confidenceChatPlugin.provide(
    createToolExtension({
      id: `web-research-${tool.replaceAll('_', '-')}`,
      predicate: name => name === `mcp__confidence__${tool}`,
      icon: RiGlobalLine,
      renderIcon: tool === 'web_fetch' ? renderWebFetchIcon : undefined,
      display: 'grouped',
      collapseConsecutiveCalls: false,
      render: () => null,
      renderLabel: (entry, options) => (
        <WebResearchToolLabel tool={tool} entry={entry} options={options} />
      ),
    }),
  );

export const WebSearchToolExtension = createWebResearchExtension('web_search');
export const WebFetchToolExtension = createWebResearchExtension('web_fetch');
